import { z } from "zod";
import { MoneySchema } from "./catalog.js";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, SourceRefSchema } from "./source.js";
export const QuoteCurrencySchema = z.literal("GBP");
export const GbpMoneySchema = MoneySchema.superRefine((value, ctx) => {
    if (value.currency !== "GBP") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "quoting currently supports GBP only"
        });
    }
});
export const QuoteProductGroupSchema = z.enum([
    "control",
    "room_interfaces",
    "heating",
    "network_power",
    "metering",
    "extensions",
    "other"
]);
export const QuoteProductGroupMappingSchema = z
    .object({
    status: z.enum(["mapped", "unmapped"]),
    ruleId: NonEmptyStringSchema,
    source: z.enum(["bom_item_kind", "catalog_family", "product_part_no", "fallback"]),
    message: NonEmptyStringSchema.optional()
})
    .strict();
export const PriceSourceSchema = z
    .object({
    kind: z.enum(["catalog_gold_price_list", "manual_rich_override"]),
    productPartNo: NonEmptyStringSchema,
    canonicalProductPartNo: NonEmptyStringSchema,
    catalogReleaseId: IdSchema,
    catalogVersion: NonEmptyStringSchema,
    priceListName: NonEmptyStringSchema,
    priceListVersion: NonEmptyStringSchema,
    priceListDate: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema).min(1)
})
    .strict();
export const PricingContextSchema = z
    .object({
    currency: QuoteCurrencySchema,
    catalogReleaseId: IdSchema,
    catalogVersion: NonEmptyStringSchema,
    priceListName: NonEmptyStringSchema,
    priceListVersion: NonEmptyStringSchema,
    priceListDate: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema).min(1)
})
    .strict();
export const MissingPriceReasonSchema = z.enum([
    "catalog_product_missing",
    "catalog_product_has_no_price",
    "excluded_by_scope",
    "covered_by_existing",
    "not_a_product_quantity",
    "currency_unsupported"
]);
export const PricedBomPricingSchema = z.discriminatedUnion("status", [
    z
        .object({
        status: z.literal("priced"),
        unitPrice: GbpMoneySchema,
        lineTotal: GbpMoneySchema,
        priceSource: PriceSourceSchema
    })
        .strict(),
    z
        .object({
        status: z.literal("missing_price"),
        reason: MissingPriceReasonSchema,
        message: NonEmptyStringSchema,
        priceListVersion: NonEmptyStringSchema,
        priceListDate: NonEmptyStringSchema,
        productPartNo: NonEmptyStringSchema.optional(),
        sourceRefs: z.array(SourceRefSchema)
    })
        .strict(),
    z
        .object({
        status: z.literal("excluded"),
        reason: MissingPriceReasonSchema,
        message: NonEmptyStringSchema,
        sourceRefs: z.array(SourceRefSchema)
    })
        .strict()
]);
export const PricedControlsBomRowSchema = z
    .object({
    id: IdSchema,
    apartmentTypeId: IdSchema,
    itemClass: z.enum(["controller", "device", "infrastructure"]),
    itemKind: NonEmptyStringSchema,
    productPartNo: NonEmptyStringSchema.optional(),
    description: NonEmptyStringSchema,
    supplySource: z.enum(["titan-supplied", "fabricated", "in-house", "third-party"]),
    provisionScope: z.enum(["apartment_type", "project"]).optional(),
    installIncluded: z.boolean().optional(),
    provisionScopeBasis: NonEmptyStringSchema.optional(),
    installIncludedBasis: NonEmptyStringSchema.optional(),
    quantityPerApartmentType: z.number().int().nonnegative(),
    apartmentTypeQuantity: z.number().int().positive(),
    projectQuantity: z.number().int().nonnegative(),
    why: NonEmptyStringSchema,
    how: NonEmptyStringSchema,
    installNotes: z.array(NonEmptyStringSchema),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(IdSchema),
    riskRefs: z.array(IdSchema),
    specItemRefs: z.array(IdSchema),
    canonicalProductPartNo: NonEmptyStringSchema.optional(),
    productGroup: QuoteProductGroupSchema,
    productGroupMapping: QuoteProductGroupMappingSchema,
    groupingIssueIds: z.array(IdSchema),
    pricing: PricedBomPricingSchema,
    pricingIssueIds: z.array(IdSchema)
})
    .strict();
export const QuotePricingIssueSchema = z
    .object({
    id: IdSchema,
    severity: z.enum(["warning", "blocker"]),
    rowId: IdSchema,
    reason: MissingPriceReasonSchema,
    message: NonEmptyStringSchema,
    productPartNo: NonEmptyStringSchema.optional(),
    sourceRefs: z.array(SourceRefSchema),
    createdAt: IsoTimestampSchema
})
    .strict();
export const QuoteGroupingIssueSchema = z
    .object({
    id: IdSchema,
    severity: z.literal("warning"),
    rowId: IdSchema,
    productGroup: QuoteProductGroupSchema,
    reason: z.literal("product_group_unmapped"),
    message: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema),
    createdAt: IsoTimestampSchema
})
    .strict();
export const QuoteSubtotalSchema = z
    .object({
    label: NonEmptyStringSchema,
    currency: QuoteCurrencySchema,
    pricedLineCount: z.number().int().nonnegative(),
    missingPriceLineCount: z.number().int().nonnegative(),
    excludedLineCount: z.number().int().nonnegative(),
    amountExVat: z.number().nonnegative(),
    sourceRowIds: z.array(IdSchema)
})
    .strict();
export const ApartmentTypeQuoteSummarySchema = z
    .object({
    apartmentTypeId: IdSchema,
    apartmentTypeQuantity: z.number().int().positive(),
    currency: QuoteCurrencySchema,
    amountExVatPerApartmentType: z.number().nonnegative(),
    amountExVatProjectContribution: z.number().nonnegative(),
    subtotalsByProductGroup: z.record(QuoteProductGroupSchema, QuoteSubtotalSchema),
    missingPriceLineCount: z.number().int().nonnegative()
})
    .strict();
export const ProjectScopeQuoteSummarySchema = z
    .object({
    scope: z.literal("whole_job"),
    label: z.literal("Whole job"),
    currency: QuoteCurrencySchema,
    amountExVat: z.number().nonnegative(),
    subtotalsByProductGroup: z.record(QuoteProductGroupSchema, QuoteSubtotalSchema),
    missingPriceLineCount: z.number().int().nonnegative(),
    excludedLineCount: z.number().int().nonnegative(),
    sourceRowIds: z.array(IdSchema)
})
    .strict();
export const QuoteTotalsSchema = z
    .object({
    currency: QuoteCurrencySchema,
    amountExVat: z.number().nonnegative(),
    vat: z
        .object({
        status: z.literal("not_calculated"),
        message: NonEmptyStringSchema
    })
        .strict(),
    byApartmentType: z.array(ApartmentTypeQuoteSummarySchema),
    projectScope: ProjectScopeQuoteSummarySchema,
    subtotalsByProductGroup: z.record(QuoteProductGroupSchema, QuoteSubtotalSchema),
    subtotalsBySupplySource: z.record(z.enum(["titan-supplied", "fabricated", "in-house", "third-party"]), QuoteSubtotalSchema),
    missingPriceIssueIds: z.array(IdSchema),
    excludedLineIds: z.array(IdSchema),
    completeness: z.enum(["complete", "incomplete_missing_prices"])
})
    .strict();
//# sourceMappingURL=quoting.js.map